import {
  Container,
  Stack,
  Box,
  Text,
  Heading,
  HStack,
  Card,
  CardBody,
  Badge,
  Wrap,
  useColorModeValue,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import SectionHeading from "./SectionHeading";
import t from "../translations";

const MotionDiv = motion.div;

function EducationCard({ entry, index, color }) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const cardBg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.600", "gray.400");
  const dotBg = useColorModeValue(`${color}.400`, `${color}.300`);
  const glowColor = useColorModeValue(
    `var(--chakra-colors-${color}-200)`,
    `var(--chakra-colors-${color}-400)`
  );

  return (
    <Box position="relative" pl={{ base: 8, md: 10 }}>
      {/* Timeline dot */}
      <Box
        position="absolute"
        left={{ base: "4px", md: "6px" }}
        top="24px"
        w={3}
        h={3}
        borderRadius="full"
        bg={dotBg}
        zIndex={1}
        boxShadow={`0 0 8px ${glowColor}`}
      />

      <MotionDiv
        ref={ref}
        initial={{ opacity: 0, y: 30 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{
          duration: 0.5,
          delay: index * 0.15,
          ease: "easeOut",
        }}
        whileHover={{ y: -4 }}
        style={{ borderRadius: "var(--chakra-radii-xl)" }}
      >
        <Card
          bg={cardBg}
          borderRadius="xl"
          transition="all 0.3s ease"
          _hover={{
            boxShadow: `0 4px 20px 4px ${glowColor}`,
          }}
        >
          <CardBody>
            <Stack spacing={4}>
              <HStack justify="space-between" flexWrap="wrap" gap={2}>
                <Box>
                  <Heading size={{ base: "sm", md: "md" }}>{entry.institution}</Heading>
                  <Text color={`${color}.400`} fontWeight={600} mt={1}>
                    {entry.degree}
                  </Text>
                </Box>
                <Badge
                  colorScheme={color}
                  borderRadius="full"
                  px={3}
                  py={1}
                  fontSize="sm"
                >
                  {entry.duration}
                </Badge>
              </HStack>
              <Text color={textColor} fontSize="md" lineHeight={1.8}>
                {entry.description}
              </Text>
              <Wrap spacing={2}>
                {entry.highlights.map((item) => (
                  <Badge
                    key={item}
                    colorScheme="gray"
                    fontSize="xs"
                    px={3}
                    py={1}
                    borderRadius="full"
                  >
                    {item}
                  </Badge>
                ))}
              </Wrap>
            </Stack>
          </CardBody>
        </Card>
      </MotionDiv>
    </Box>
  );
}

export default function Education({ color = "purple" }) {
  const lineColor = useColorModeValue("gray.200", "gray.600");
  const dotBg = useColorModeValue(`${color}.400`, `${color}.300`);
  const glowColor = useColorModeValue(
    `var(--chakra-colors-${color}-200)`,
    `var(--chakra-colors-${color}-400)`
  );

  return (
    <Container maxW="4xl" id="education" py={{ base: 16, md: 24 }}>
      <Stack spacing={{ base: 6, md: 10 }}>
        <SectionHeading>{t.education.heading}</SectionHeading>

        <Box position="relative" px={4}>
          {/* Timeline vertical line — extends from first dot to closing dot */}
          <Box
            position="absolute"
            left={{ base: "24px", md: "26px" }}
            top="24px"
            bottom="0px"
            w="2px"
            bg={lineColor}
          />

          <Stack spacing={6}>
            {t.education.entries.map((entry, index) => (
              <EducationCard
                key={entry.institution}
                entry={entry}
                index={index}
                color={color}
              />
            ))}
          </Stack>

          {/* Closing dot to cap off the timeline */}
          <Box position="relative" pl={{ base: 8, md: 10 }} h="12px">
            <Box
              position="absolute"
              left={{ base: "4px", md: "6px" }}
              top="0px"
              w={3}
              h={3}
              borderRadius="full"
              bg={dotBg}
              boxShadow={`0 0 8px ${glowColor}`}
              zIndex={1}
            />
          </Box>
        </Box>
      </Stack>
    </Container>
  );
}
